import { AlertTriangle, Info, ShieldAlert, Scale } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmtRp, fmtRpShort } from "@/lib/format";
import { KpiCard, Row, SectionCard } from "@/components/common/Cards";
import { DeltaBadge } from "./Board";

const LEVEL = {
  danger: { icon: ShieldAlert, cls: "border-[hsl(0_50%_38%)] bg-[hsl(0_40%_9%)] text-[hsl(0_70%_80%)]", label: "Risiko tinggi" },
  warning: { icon: AlertTriangle, cls: "border-[hsl(38_70%_40%)] bg-[hsl(38_45%_9%)] text-[hsl(40_80%_78%)]", label: "Perhatian" },
  info: { icon: Info, cls: "border-border bg-[hsl(220_10%_8%)] text-[hsl(45_15%_82%)]", label: "Info" },
};

const ROWS = [
  ["omzet", "Omzet (peredaran bruto)"],
  ["laba_komersial", "Laba komersial"],
  ["koreksi_fiskal", "Koreksi fiskal (+)"],
  ["kompensasi_rugi", "Kompensasi rugi fiskal"],
  ["pkp", "Penghasilan Kena Pajak"],
  ["pph_badan", "PPh Badan"],
  ["pph_final", "PPh final (4(2) dll.)"],
  ["ppn_non_kredit", "PPN tidak dapat dikreditkan"],
  ["pph23_kredit", "Kredit PPh 23 dipotong pihak lain"],
  ["total_beban_pajak", "Total beban pajak"],
  ["laba_setelah_pajak", "Laba setelah pajak"],
];

export function SimulationResult({ result, stale }) {
  if (!result) return null;
  const g = result.group;
  const chart = result.companies.map((c) => ({
    name: c.name.length > 18 ? `${c.name.slice(0, 17)}…` : c.name,
    Sebelum: c.before.total_beban_pajak,
    Sesudah: c.after.total_beban_pajak,
  }));
  const saving = g.hemat_bersih;

  return (
    <div className="space-y-5" data-testid="eq-sim-result">
      {stale && (
        <div className="rounded-lg border border-[hsl(38_70%_40%)] bg-[hsl(38_45%_9%)] px-3 py-2 text-xs text-[hsl(40_80%_78%)]" data-testid="eq-sim-stale">
          Pemindahan berubah sejak simulasi terakhir — klik Simulasi Pajak lagi untuk memperbarui hasil.
        </div>
      )}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard label="Pajak grup sebelum" value={fmtRpShort(g.pajak_sebelum)} sub={fmtRp(g.pajak_sebelum)} testid="eq-kpi-before" icon={Scale} />
        <KpiCard label="Pajak grup sesudah" value={fmtRpShort(g.pajak_sesudah)} sub={fmtRp(g.pajak_sesudah)} testid="eq-kpi-after" icon={Scale} />
        <KpiCard
          label={saving >= 0 ? "Hemat bersih grup" : "Tambahan pajak grup"}
          value={fmtRpShort(Math.abs(saving))}
          sub={`${fmtRp(Math.abs(saving))} · ${g.jumlah_transfer} pemindahan`}
          tone={saving > 0 ? "good" : saving < 0 ? "bad" : undefined}
          accent
          testid="eq-kpi-saving"
        />
        <KpiCard
          label="Laba setelah pajak grup"
          value={fmtRpShort(g.laba_setelah_pajak_sesudah)}
          sub={`Sebelumnya ${fmtRpShort(g.laba_setelah_pajak_sebelum)}`}
          testid="eq-kpi-net"
        />
      </div>

      <div className="grid xl:grid-cols-[1fr_420px] gap-5">
        <SectionCard title="Dampak per PT" description="Sebelum → sesudah ekualisasi (tahunan)." testid="eq-sim-companies">
          <div className="grid md:grid-cols-2 gap-4">
            {result.companies.map((c) => (
              <div key={c.id} className="rounded-xl border border-border bg-[hsl(220_10%_7%)] p-4" data-testid={`eq-sim-company-${c.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-display text-lg leading-tight">{c.name}</div>
                  <DeltaBadge value={c.delta_pajak} testid={`eq-sim-company-${c.id}-delta`} />
                </div>
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 text-[10px] uppercase tracking-wider text-muted-foreground pb-1 border-b border-border">
                  <span>Pos</span>
                  <span className="text-right w-20">Sebelum</span>
                  <span className="text-right w-20">Sesudah</span>
                </div>
                {ROWS.map(([k, label]) => {
                  const b = c.before[k];
                  const a = c.after[k];
                  const strong = k === "total_beban_pajak" || k === "laba_setelah_pajak";
                  return (
                    <div key={k} className={`grid grid-cols-[1fr_auto_auto] gap-x-3 py-1.5 border-b border-border/60 last:border-0 text-xs ${strong ? "font-semibold" : ""}`}>
                      <span className={strong ? "text-foreground" : "text-[hsl(45_15%_80%)]"}>{label}</span>
                      <span className="num text-right w-20 text-muted-foreground">{fmtRpShort(b)}</span>
                      <span className={`num text-right w-20 ${a !== b ? "text-primary" : "text-foreground"}`}>{fmtRpShort(a)}</span>
                    </div>
                  );
                })}
                <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">{c.after.pph_badan_detail}</p>
              </div>
            ))}
          </div>
        </SectionCard>

        <div className="space-y-5">
          <SectionCard title="Beban pajak per PT" testid="eq-sim-chart">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} initialDimension={{ width: 380, height: 224 }}>
                <BarChart data={chart} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                  <CartesianGrid stroke="hsl(220 10% 16%)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "hsl(45 10% 65%)", fontSize: 10 }} />
                  <YAxis tickFormatter={(v) => fmtRpShort(v).replace("Rp ", "")} tick={{ fill: "hsl(45 10% 65%)", fontSize: 10 }} width={56} />
                  <Tooltip formatter={(v) => fmtRp(v)} contentStyle={{ background: "hsl(220 12% 9%)", border: "1px solid hsl(220 10% 20%)", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Sebelum" fill="hsl(220 8% 45%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Sesudah" fill="hsl(43 74% 56%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
          <SectionCard title={`Catatan kepatuhan (${result.warnings.length})`} description="Transfer pricing, koreksi fiskal, PPN & fasilitas." testid="eq-sim-warnings">
            {result.warnings.length === 0 ? (
              <p className="text-xs text-muted-foreground">Tidak ada catatan.</p>
            ) : (
              <ul className="space-y-2">
                {result.warnings.map((w, i) => {
                  const L = LEVEL[w.level] || LEVEL.info;
                  return (
                    <li key={i} className={`rounded-lg border px-3 py-2 text-xs flex gap-2 ${L.cls}`} data-testid={`eq-warning-${i}`}>
                      <L.icon className="h-4 w-4 shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{w.text}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="eq-disclaimer">{result.disclaimer}</p>
    </div>
  );
}

export { Row };
